import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { DialogRef, DialogService, KENDO_DIALOGS } from '@progress/kendo-angular-dialog';
import { TranslationPipe } from '@raptor-fe/services';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
    selector: 'lib-branch-time-zone-modal',
    template: `
        <kendo-dialog width="520px" (close)="onCancel()">
            <kendo-dialog-titlebar>
                <h6 class="mb-0">{{ { feature: 'Tenant Management', component: 'Branch Time Zone Modal', label: 'Confirm store time zone' } | translate }}</h6>
            </kendo-dialog-titlebar>

            <p class="mb-3">
                {{ { feature: 'Tenant Management', component: 'Branch Time Zone Modal', label: 'Booking times shown to customers use the store time zone. Confirm it before saving.' } | translate }}
            </p>

            <label for="modalBranchTimeZone" class="fw-bold mb-1">
                {{ { feature: 'Tenant Management', component: 'Branch Time Zone Modal', label: 'Time Zone' } | translate }}
            </label>
            <select id="modalBranchTimeZone" class="form-select" (change)="onSelect($event)">
                <option value="" [selected]="!selected()">
                    {{ { feature: 'Tenant Management', component: 'Branch Time Zone Modal', label: 'Select a time zone' } | translate }}
                </option>
                @for (zone of timeZones; track zone) {
                    <option [value]="zone" [selected]="zone === selected()">{{ zone }}</option>
                }
            </select>

            <p class="mb-0 mt-2 small opacity-75">
                @if (currentTimeZoneId) {
                    {{ { feature: 'Tenant Management', component: 'Branch Time Zone Modal', label: 'Currently' } | translate }}: {{ currentTimeZoneId }}
                } @else {
                    {{ { feature: 'Tenant Management', component: 'Branch Time Zone Modal', label: 'This store has no time zone set. The pre-selected value was detected from your browser.' } | translate }}
                }
            </p>

            <kendo-dialog-actions layout="end">
                <button class="btn btn-sm btn-secondary min-w-24" (click)="onCancel()">
                    {{ { feature: 'Common', component: 'Button', label: 'Cancel' } | translate }}
                </button>
                <button class="btn btn-sm btn-primary min-w-24" [disabled]="!selected()" (click)="onConfirm()">
                    {{ { feature: 'Common', component: 'Button', label: 'Confirm' } | translate }}
                </button>
            </kendo-dialog-actions>
        </kendo-dialog>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslationPipe, KENDO_DIALOGS],
})
export class BranchTimeZoneModalComponent {
    protected readonly dialogRef = inject(DialogRef);

    timeZones: string[] = [];

    /** The persisted zone, or null when the store has none and a detected zone is being proposed. */
    currentTimeZoneId: string | null = null;

    protected readonly selected = signal('');

    /**
     * Prompts for the zone a save is about to persist, so a value the user has not agreed to never reaches a
     * customer-facing booking time. Emits the chosen zone, or null when the save is abandoned.
     */
    static open(
        dialogService: DialogService,
        timeZones: string[],
        proposed: string,
        current: string | null,
    ): Observable<string | null> {
        const dialogRef = dialogService.open({
            content: BranchTimeZoneModalComponent,
            width: '520px',
        });

        const instance = dialogRef.content.instance as BranchTimeZoneModalComponent;
        instance.timeZones = timeZones;
        instance.currentTimeZoneId = current;
        instance.setProposed(proposed);

        return dialogRef.result.pipe(map((result) => (typeof result === 'string' && result ? result : null)));
    }

    setProposed(timeZoneId: string): void {
        this.selected.set(timeZoneId);
    }

    protected onSelect(event: Event): void {
        this.selected.set((event.target as HTMLSelectElement).value);
    }

    protected onConfirm(): void {
        const timeZoneId = this.selected();

        if (!timeZoneId) {
            return;
        }

        this.dialogRef.close(timeZoneId);
    }

    protected onCancel(): void {
        this.dialogRef.close(null);
    }
}
